import { Stack, Typography, useTheme } from "@mui/material";
import type { Control, FieldValues, Path } from "react-hook-form";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import { DisclosureSection } from "web_myplann/Shared/Components/DisclosureSection";
import { FlatRowFormDatePicker } from "web_myplann/Shared/Components/FlatRow/FlatRowFormDatePicker";
import { FlatRowFormInput } from "web_myplann/Shared/Components/FlatRow/FlatRowFormInput";
import { DateView } from "web_myplann/Shared/Types/DateView";
import { DateFormat } from "web_myplann/Shared/Utils/dateFormats";

type InvestmentValuesSectionProps<
	FormType extends FieldValues,
	TContext,
	TTransformed extends FieldValues | undefined,
> = {
	control: Control<FormType, TContext, TTransformed>;
};

const PageResources = Resources.Portfolio.AddInvestment;

export const InvestmentValuesSection = <
	FormType extends FieldValues,
	TContext,
	TTransformed extends FieldValues | undefined,
>({
	control,
}: InvestmentValuesSectionProps<FormType, TContext, TTransformed>) => {
	const theme = useTheme();
	const { t } = useResource();

	return (
		<>
			<Typography variant="sectionLabel">
				{t(PageResources.Sections.values)}
			</Typography>
			<Stack bgcolor={theme.palette.background.paper}>
				<DisclosureSection
					labelCollapsed={t(PageResources.Sections.showValues)}
					labelExpanded={t(PageResources.Sections.hideValues)}
				>
					<FlatRowFormInput
						control={control}
						name={"value" as Path<FormType>}
						type="number"
						label={t(PageResources.Fields.value)}
						placeholder={t(Resources.Portfolio.AddShared.Placeholders.optional)}
						suffix={t(Resources.Common.CurrencySign)}
					/>
					<FlatRowFormInput
						control={control}
						name={"invested" as Path<FormType>}
						type="number"
						label={t(PageResources.Fields.invested)}
						placeholder={t(Resources.Portfolio.AddShared.Placeholders.optional)}
						suffix={t(Resources.Common.CurrencySign)}
					/>
					<FlatRowFormDatePicker
						control={control}
						name={"valueDate" as Path<FormType>}
						label={t(PageResources.Fields.valueDate)}
						views={[DateView.Year, DateView.Month, DateView.Day]}
						displayFormat={DateFormat.Display}
						maxDate={new Date()}
						isLast
					/>
				</DisclosureSection>
			</Stack>
		</>
	);
};
